import type { IEnrichableEntity } from '@talos/kyoko';
import type { AssetLeaderboardRow } from './AssetLeaderboard.types';

/**
 * One live-price enrichment for a single asset, keyed by CoinMetrics asset id. This is the only
 * enrichment the Asset Leaderboard has today (real-time `ReferenceRateUSD` via the CoinMetrics websocket);
 * modelling it as an enrichment — rather than mutating the row's `price` in place — is what lets the
 * blotter's visible-rows enrichment pipeline (`useBlotterVisibleRowsEnrichment`) push per-tick updates
 * as row-level deltas instead of re-diffing the whole universe on every message.
 */
export interface AssetLeaderboardPriceEnrichment {
  cmAssetId: string;
  price: number;
}

/**
 * Row entity for the Asset Leaderboard blotter, wrapping the REST-built `AssetLeaderboardRow` and layering the
 * live websocket price on top via the `IEnrichableEntity` contract. Every base field is exposed
 * verbatim from the underlying row; only `price` is overlaid — it returns the live tick when one has
 * been applied, otherwise the last REST snapshot close. 1h/24h/7d change and the sparkline deliberately
 * stay pinned to the REST snapshot (they represent daily trend, not intraday noise), so they read
 * straight through from the row and are never affected by an enrichment.
 *
 * Field access (`data.price`, `data.priceHistory`, …) resolves through these getters, which is what
 * AG Grid's `field`-based columns and cell renderers read.
 */
export class AssetLeaderboardEntity implements IEnrichableEntity<
  AssetLeaderboardEntity,
  AssetLeaderboardPriceEnrichment
> {
  private livePrice: number | null = null;
  private lastPriceTrend: -1 | 0 | 1 = 0;

  constructor(private readonly row: AssetLeaderboardRow) {}

  get id(): string {
    return this.row.id;
  }

  get symbol(): string {
    return this.row.symbol;
  }

  get cmAssetId(): string {
    return this.row.cmAssetId;
  }

  /** Live websocket price when one has landed, otherwise the last REST snapshot close. */
  get price(): number | null {
    return this.livePrice ?? this.row.price;
  }

  /**
   * Direction of the most recent live price tick relative to the one before it: 1 up, -1 down, 0 when
   * unknown (no live tick yet, or the price hasn't moved between ticks). Drives the green/red trend
   * icon in the Price cell, mirroring the Watchlist ref-rate ticker. Deliberately null-of-direction
   * until a second distinct tick lands, so we never infer a direction by comparing the first live tick
   * against the (different-cadence) REST close.
   */
  get priceTrend(): -1 | 0 | 1 {
    return this.lastPriceTrend;
  }

  get change1hPct(): number | null {
    return this.row.change1hPct;
  }

  get change24hPct(): number | null {
    return this.row.change24hPct;
  }

  get change7dPct(): number | null {
    return this.row.change7dPct;
  }

  get spotVolumeUsd(): number | null {
    return this.row.spotVolumeUsd;
  }

  get marketCapUsd(): number | null {
    return this.row.marketCapUsd;
  }

  get priceHistory(): number[] {
    return this.row.priceHistory;
  }

  addEnrichments(enrichments: AssetLeaderboardPriceEnrichment[]): void {
    for (const enrichment of enrichments) {
      if (enrichment.cmAssetId === this.cmAssetId) {
        // Only a genuine move between two live ticks sets a direction. The first tick (no prior live
        // price) and a re-applied identical price (e.g. carrying the last tick onto a rebuilt row) leave
        // the arrow untouched, so the icon reflects real intraday movement rather than snapshot drift.
        if (this.livePrice != null && enrichment.price !== this.livePrice) {
          this.lastPriceTrend = enrichment.price > this.livePrice ? 1 : -1;
        }
        this.livePrice = enrichment.price;
      }
    }
  }

  removeEnrichments(ids: string[]): void {
    if (ids.includes(this.cmAssetId)) {
      this.livePrice = null;
      this.lastPriceTrend = 0;
    }
  }

  clearEnrichments(): void {
    this.livePrice = null;
    this.lastPriceTrend = 0;
  }

  getEnrichments(): AssetLeaderboardPriceEnrichment[] {
    return this.livePrice != null ? [{ cmAssetId: this.cmAssetId, price: this.livePrice }] : [];
  }

  getEnrichmentRequirements(): string[] {
    return [this.cmAssetId];
  }

  hasEnrichment(id: string): boolean {
    return this.livePrice != null && id === this.cmAssetId;
  }

  clone(): AssetLeaderboardEntity {
    const clone = new AssetLeaderboardEntity(this.row);
    clone.livePrice = this.livePrice;
    clone.lastPriceTrend = this.lastPriceTrend;
    return clone;
  }
}
